<template>
  <v-container>
    <v-card>
      <!-- Header Section -->
      <v-card-title
        class="d-flex justify-space-between align-center flex-wrap gap-4"
      >
        <div v-if="personId === null" class="d-flex align-center">
          <span class="text-h6 font-weight-bold">Event Subscriptions</span>
        </div>
        <v-spacer />
        <v-btn
          icon
          color="primary"
          @click="openDialog(null)"
          class="ml-2"
          style="margin-left: auto"
          title="Add Relationship"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Data Table Section -->
      <v-data-table
        v-model="selected"
        :show-select="personId === null"
        :headers="headers"
        :items="filteredClubPersonRel"
        item-key="seqNo"
        class="elevation-2"
        dense
        :items-per-page="500"
        :sort-by="'updateDate'"
        :sort-desc="true"
        :group-by="personId === null ? 'event.name' : undefined"
        :show-group-by="personId === null"
        :hide-default-footer="personId !== null"
      >
        <!-- <template v-slot:item.person.fullName="{ item }">
          <span>{{ item.person.firstName }} {{ item.person.lastName }}</span>
        </template> -->
        <template v-slot:[`item.tryoutDatetime`]="{ item }">
          <div>
            <td class="truncate-for-print">
              {{ formatDatetime(item.tryoutDatetime) }}
            </td>
          </div>
        </template>

        <template v-slot:[`item.additionalInfo`]="{ item }">
          <v-tooltip bottom max-width="400px">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="text-truncate">
                <v-chip 
                  v-if="getCommunicationCount(item.additionalInfo) > 0"
                  small
                  color="primary"
                  text-color="white"
                >
                  {{ getCommunicationCount(item.additionalInfo) }} entries
                </v-chip>
                <span v-else class="text--disabled">No communications</span>
              </div>
            </template>
            <div class="communication-tooltip">
              {{ getLatestCommunication(item.additionalInfo) }}
            </div>
          </v-tooltip>
        </template>

        <template v-slot:item.formsSigned="{ item }">
          <FormsSignedCell :forms-signed="item.formsSigned" />
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu offset-y open-on-hover>
            <template v-slot:activator="{ on, attrs }">
              <v-btn x-small color="primary" dark v-bind="attrs" v-on="on">
                <v-icon small>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="openDialog(item)">
                <v-list-item-icon>
                  <v-icon>edit</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>
              <v-list-item @click="confirmDelete(item.seqNo)">
                <v-list-item-icon>
                  <v-icon>delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-results>
          <v-alert type="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
        </template>

        <template v-slot:[`item.participationDays`]="{ item }">
          <ParticipationDaysShort
            :participation-days="item.participationDays"
            :workout-schedules="workoutSchedules"
            :workout-schedule-label="workoutScheduleLabel"
          />
        </template>
      </v-data-table>
    </v-card>

    <!-- 📄 FormsSignedViewer Dialog -->
    <v-dialog v-model="dialog" max-width="800px" scrollable persistent>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between">
          <div>
            <div class="text-h6 font-weight-bold">
              {{ form.seqNo ? "Edit" : "Add" }} Relationship
            </div>
            <div
              v-if="form.person && form.person.personId"
              class="subtitle-2 text-muted"
            >
              for {{ form.person.firstName }} {{ form.person.lastName }}
            </div>
          </div>
          <v-btn icon @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4 px-6">
          <v-container fluid>
            <v-row dense>
              <v-col cols="12" sm="6">
                <!-- {{ form  }} -->
                <AthletesDropdown
                  @parentValueChange="form.personId = $event"
                  :parentValue="
                    Number(personId ? personId : form.person.personId)
                  "
                  :mode="'ATH'"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <EventCatalogSelect
                  v-model="form.event.eventId"
                  :clearable="true"
                  :dense="true"
                  :outlined="true"
                  eventType="term"
                  status="active"
                  @event-selected="onEventSelected"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <GroupSelect
                  v-model="form.teamId"
                  :item-value="'node.teamId'"
                  :multiple="false"
                  :rules="requiredRule"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <catValueDropDown
                  :outlined="true"
                  v-model="form.status"
                  label="Status"
                  catName="athlete_status"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <catValueDropDown
                  :outlined="true"
                  v-model="form.role"
                  label="Role"
                  catName="ROLES"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <catValueDropDown
                  :outlined="true"
                  v-model="form.volunteerPreference"
                  label="Volunteer Preference"
                  catName="volunteer_preference"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.participationDays"
                  :items="filteredWorkoutSchedules"
                  :item-text="workoutScheduleLabel"
                  item-value="seqNo"
                  label="Workout Schedule"
                  outlined
                  dense
                  clearable
                  multiple
                >
                </v-select>
              </v-col>
              <v-flex>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.tryoutDate"
                          label="Tryout Date"
                          readonly
                          prepend-inner-icon="event"
                          v-bind="attrs"
                          v-on="on"
                          clearable
                          clear-icon="close"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.tryoutDate"
                        @input="dateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="timeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.tryoutTime"
                          label="Tryout Time"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          prepend-inner-icon="access_time"
                          clearable
                          clear-icon="close"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                        v-model="form.tryoutTime"
                        format="24hr"
                        :allowed-times="allowedTimes"
                      ></v-time-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </v-flex>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="startMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.startMembership"
                      label="Start Membership"
                      readonly
                      dense
                      outlined
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.startMembership"
                    @change="startMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" sm="6">
                <v-menu
                  v-model="endMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.endMembership"
                      label="End Membership"
                      readonly
                      dense
                      outlined
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.endMembership"
                    @change="endMenu = false"
                  />
                </v-menu>
              </v-col>
               <v-col cols="12">
                <v-divider class="my-2" />
                <h4 class="text-subtitle-1 font-weight-medium mb-2">
                  Communication History
                </h4>

                <!-- ✅ KEEP: System Triggers Helper -->
                <div v-if="availableSystemTriggers.length > 0" class="mb-3">
                  <v-card outlined class="pa-3">
                    <div class="text-subtitle-2 font-weight-medium mb-2">
                      Available System AI Triggers:
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon small v-bind="attrs" v-on="on" class="ml-1">info</v-icon>
                        </template>
                        <span>Click triggers to insert into your message. These will trigger automated actions when saved.</span>
                      </v-tooltip>
                    </div>
                    
                    <!-- Search/Filter triggers -->
                    <v-text-field
                      v-model="triggerSearch"
                      label="Search triggers..."
                      dense
                      outlined
                      clearable
                      prepend-inner-icon="search"
                      class="mb-2"
                      v-if="availableSystemTriggers.length > 5"
                    />
                    
                    <!-- Trigger chips -->
                    <div class="trigger-chips">
                      <v-chip
                        v-for="trigger in filteredSystemTriggers"
                        :key="trigger"
                        small
                        outlined
                        color="primary"
                        class="ma-1 cursor-pointer"
                        @click="insertTrigger(trigger)"
                      >
                        {{ trigger }}
                      </v-chip>
                    </div>
                  </v-card>
                </div>

                <!-- ✅ SIMPLIFIED: New Communication Entry -->
                <v-textarea
                  ref="communicationTextarea"
                  v-model="newCommunication"
                  label="Add New Communication"
                  placeholder="Enter your communication notes here..."
                  dense
                  outlined
                  rows="4"
                  counter="500"
                  :rules="[v => !v || v.length <= 500 || 'Max 500 characters']"
                />

                <!-- Historical Communications Display -->
                <div v-if="communicationHistory.length > 0" class="mt-3">
                  <v-card outlined class="communication-history">
                    <v-card-text class="py-2">
                      <div
                        v-for="(entry, index) in communicationHistory"
                        :key="index"
                        class="communication-entry"
                        :class="{ 'mb-2': index < communicationHistory.length - 1 }"
                      >
                        <div class="d-flex justify-space-between align-center">
                          <div class="text-caption text--secondary">
                            <strong>{{ entry.username }}</strong> - {{ formatDate(entry.date) }}
                            <!-- ✅ KEEP: Show triggers used in history -->
                            <span v-if="getEntryTriggers(entry.message).length > 0" class="ml-2">
                              <v-chip
                                v-for="trigger in getEntryTriggers(entry.message)"
                                :key="trigger"
                                x-small
                                color="primary"
                                text-color="white"
                                class="ml-1"
                              >
                                {{ trigger }}
                              </v-chip>
                            </span>
                          </div>
                        </div>
                        <div 
                          class="text-body-2 mt-1"
                          v-html="highlightTriggersInMessage(entry.message, availableSystemTriggers)"
                        ></div>
                        <v-divider v-if="index < communicationHistory.length - 1" class="mt-2" />
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Legacy Additional Info (if exists) -->
                <v-text-field
                  v-if="form.additionalInfo && !isNewHistoryFormat"
                  v-model="form.additionalInfo"
                  label="Legacy Additional Info"
                  dense
                  outlined
                  readonly
                  hint="This is legacy data. New communications will be added to history above."
                  persistent-hint
                  class="mt-3"
                />
              </v-col>

              <v-col cols="12" v-if="form.formsSigned">
                <v-divider class="my-2" />
                <h4 class="text-subtitle-1 font-weight-medium mb-2">
                  Signed Forms
                </h4>
                <FormsSignedViewer :value="form.formsSigned" />
              </v-col>

            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-6 py-4">
          <v-spacer />
          <v-btn color="grey darken-1" text @click="dialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" class="elevation-1" @click="saveRel">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>



<script>
import { mapGetters, mapActions } from "vuex";
import FormsSignedViewer from "./FormsSignedViewer.vue";
import catValueDropDown from "../../shared/cat_val_dropdown.vue";
import EventCatalogSelect from "../../shared/selectEventCatalog.vue";
import GroupSelect from "../../shared/selectGroup.vue";
import AthletesDropdown from "@/components/shared/athletes_dropdown.vue";
import ParticipationDaysShort from "@/components/shared/ParticipationDaysShort.vue";
import { getCatValues } from "../../../mixins/getCatValues.js";

import { 
  hasJsonStructure, 
  formatDatetime, 
  workoutScheduleLabel,
  parseParticipationDays, 
  getWorkoutScheduleById,
  getCommunicationCount,
  getLatestCommunication,
  getSystemTriggers,
  extractTriggersFromMessage,
  highlightTriggersInMessage  // ✅ KEEP: Still used in history display
} from "../../../_helpers/index";

import FormsSignedCell from "@/components/shared/FormsSignedCell.vue";

export default {
    mixins: [getCatValues],
  components: {
    FormsSignedViewer,
    catValueDropDown,
    GroupSelect,
    EventCatalogSelect,
    AthletesDropdown,
    ParticipationDaysShort,
    FormsSignedCell,
  },
  props: {
    personId: {
      type: [Number],
      default: null,
    },
    search: {
      type: String,
      default: "",
    },
    statusSelected: {
      type: Array,
      //default: () => ["active"],
    },
  },
  data() {
    return {
      dialog: false,      
      selectedFormsSigned: null,
      dateMenu: false,
      timeMenu: false,
      newCommunication: '',
      form: {
        person: { personId: null, firstName: "", lastName: "" },
        event: { eventId: null },
        // ...add other default fields as needed
      },
      startMenu: false,
      endMenu: false,
      selected: [],
      allowedTimes: ["17:00", "18:00", "19:00"],
      filters: {
        name: "",
        group: "",
        eventType: "",
        status: "",
      },
      requiredRule: [(v) => !!v || "Value is required"],
      headers: [
        { text: "Actions", value: "actions", sortable: false },
        // Only show these columns if personId is null
        ...(this.personId === null
          ? [
              { text: "Person", value: "person.fullName", sortable: false },
              { text: "ID", value: "person.personId" },
              { text: "RegState", value: "person.registrationState" },
              { text: "Email", value: "person.email" },
              { text: "Birth Dt", value: "person.birthDate" },
              { text: "Create Dt", value: "person.createDate" },
              // { text: "Start", value: "event.startDate" },
              // { text: "End", value: "event.endDate" },
              { text: "Event Type", value: "event.eventType" },
              { text: "Role", value: "role" },
            ]
          : []),
        { text: "Event", value: "event.name" },
        { text: "Group", value: "group.teamName" },
        { text: "Status", value: "status" },
        { text: "Days", value: "participationDays" },
        { text: "Tryout", value: "tryoutDatetime" },
        { text: "Additional Info", value: "additionalInfo" },
        { text: "Forms", value: "formsSigned" },
        { text: "updateDate", value: "updateDate" },
      ],
      triggerSearch: '', // ✅ ADD: For filtering triggers
    };
  },
  created() {
    this.allWorkoutSchedules();
  },
  computed: {
    ...mapGetters([
      "club",
      "clubPersonRel",
      "user",
      "groupList",
      "workoutSchedules",
    ]),
    filteredWorkoutSchedules() {
      const groupId =
        this.form.group && this.form.group.teamId
          ? this.form.group.teamId
          : this.form.teamId;
      const eventId =
        this.form.event && this.form.event.eventId
          ? this.form.event.eventId
          : this.form.eventId;
      //     console.log("WorkoutSchedules", this.workoutSchedules);
      //     console.log("FilteredWorkoutSchedules", this.workoutSchedules.filter(
      //   (ws) => ws.groupId === groupId && ws.eventId === eventId
      // ));
      return this.workoutSchedules.filter(
        (ws) => ws.groupId === groupId && ws.eventId === eventId
      );
    },
    selectedEventType: {
      get() {
        return this.$route.query.eventType || null;
      },
      set(value) {
        const query = { ...this.$route.query, eventType: value || undefined };
        this.$router.replace({ query });
      },
    },
    selectedEvent: {
      get() {
        const val = this.$route.query.event;
        if (!val) return [];
        return Array.isArray(val)
          ? val.map(Number)
          : val.split(",").map(Number);
      },
      set(value) {
        const query = { ...this.$route.query };
        if (Array.isArray(value) && value.length > 0) {
          query.event = value.join(",");
        } else {
          delete query.event;
        }
        this.$router.replace({ query });
      },
    },
    selectedGroup: {
      get() {
        return this.$route.query.group || null;
      },
      set(value) {
        const query = { ...this.$route.query, group: value || undefined };
        this.$router.replace({ query });
      },
    },
    filteredClubPersonRel() {
      let rels = this.clubPersonRel.map((item) => ({
        ...item,
        person: {
          ...item.person,
          fullName: `${item.person.firstName} ${item.person.lastName}`,
        },
      })); //.filter((rel) => rel.status !== "inactive");
      if (this.search) {
        const lowerSearch = this.search.toLowerCase();

        const searchInObject = (obj) => {
          if (!obj || typeof obj !== "object") return false;

          return Object.values(obj).some((val) => {
            if (typeof val === "string" || typeof val === "number") {
              return String(val).toLowerCase().includes(lowerSearch);
            }
            if (typeof val === "object" && val !== null) {
              return searchInObject(val); // recursive search in nested objects
            }
            return false;
          });
        };

        rels = rels.filter((rel) => searchInObject(rel));
      }

      if (this.personId) {
        rels = rels.filter(
          (rel) => Number(rel.person.personId) === this.personId
        );
      }
      if (this.selectedEventType) {
        rels = rels.filter(
          (rel) => rel.event && rel.event.eventType === this.selectedEventType
        );
      }
      if (this.selectedEvent && this.selectedEvent.length > 0) {
        rels = rels.filter(
          (rel) => rel.event && this.selectedEvent.includes(rel.event.eventId)
        );
      }
      if (this.selectedGroup) {
        // console.log("rels",rels)
        // console.log("selectedGroup",this.selectedGroup)
        rels = rels.filter(
          (rel) => rel.group && this.selectedGroup.includes(rel.group.teamName)
        );
      }

      if (this.statusSelected) {
        rels = rels.filter((rel) => this.statusSelected.includes(rel.status));
      }

      const lowerCaseIncludes = (val, filter) =>
        typeof val === "string" &&
        val.toLowerCase().includes(filter.toLowerCase());

      for (const [key, value] of Object.entries(this.filters)) {
        if (!value) continue;

        rels = rels.filter((rel) => {
          // Support nested keys like event.eventType
          const keys = key.split(".");
          let fieldVal = rel;

          for (const k of keys) {
            if (fieldVal && typeof fieldVal === "object") {
              fieldVal = fieldVal[k];
            } else {
              fieldVal = null;
              break;
            }
          }

          return lowerCaseIncludes(fieldVal, value);
        });
      }

      return rels;
    },
    uniqueEventTypes() {
      return [
        ...new Set(
          this.clubPersonRel
            .map((t) => t.event && t.event.eventType)
            .filter((type) => type != null) // filters out null and undefined
        ),
      ];
    },
    uniqueEvent() {
      return [
        ...new Set(
          this.clubPersonRel
            .map((t) => t.event && t.event.name)
            .filter((type) => type != null) // filters out null and undefined
        ),
      ];
    },
    uniqueGroup() {
      return [
        ...new Set(
          this.clubPersonRel
            .map((t) => t.group && t.group.teamName)
            .filter((type) => type != null) // filters out null and undefined
        ),
      ];
    },
    selectedWorkoutSchedule() {
      return this.workoutSchedules.find(
        (ws) => ws.seqNo === this.form.workoutScheduleId
      );
    },
    selectedSchedules() {
      return this.workoutSchedules.filter((ws) =>
        this.form.participationDays.includes(ws.seqNo)
      );
    },
communicationHistory() {
  if (!this.form.additionalInfo) return [];
  
  try {
    // Try to parse as JSON history format
    const parsed = JSON.parse(this.form.additionalInfo);
    if (Array.isArray(parsed)) {
      // Data is already cleaned, just sort and return
      return parsed.sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  } catch (e) {
    // If not JSON, treat as legacy single entry
    if (this.form.additionalInfo.trim()) {
      return [{
        username: 'Legacy Entry',
        date: this.form.updateDate || this.form.createDate || new Date().toISOString(),
        message: this.form.additionalInfo
      }];
    }
  }
  
  return [];
},
    isNewHistoryFormat() {
      if (!this.form.additionalInfo) return true;
      
      try {
        const parsed = JSON.parse(this.form.additionalInfo);
        return Array.isArray(parsed);
      } catch (e) {
        return false; // Legacy format
      }
    },

    // ✅ ADD: System triggers functionality
    availableSystemTriggers() {
      return getSystemTriggers(this.catValueListArray);
    },
    
    filteredSystemTriggers() {
      if (!this.triggerSearch) return this.availableSystemTriggers;
      
      const search = this.triggerSearch.toLowerCase();
      return this.availableSystemTriggers.filter(trigger =>
        trigger.toLowerCase().includes(search)
      );
    },

    
    highlightedCommunication() {
      return highlightTriggersInMessage(this.newCommunication, this.availableSystemTriggers);
    },
  },
  methods: {
    ...mapActions([
      "allWorkoutSchedules",
      "allClubPersonRel",
      "clubPersonRelMerge",
      "clubPersonRelDelete",
    ]),
    workoutScheduleLabel,
    formatDatetime,
    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    confirmMultiDelete() {
      if (
        confirm(
          `Are you sure you want to delete ${this.selected.length} record(s)?`
        )
      ) {
        this.selected.forEach((item) => {
          this.clubPersonRelDelete({ seqNo: item.seqNo });
        });
        this.selected = []; // reset selection
      }
    },
    waitUntilClubAvailable() {
      const checkInterval = setInterval(() => {
        const clubId = this.$store.state.Clubs.club;
        if (clubId) {
          clearInterval(checkInterval);
          this.loadClubPersonRel(clubId);
        }
      }, 100);
    },
    async loadClubPersonRel() {
      try {
        await this.allClubPersonRel({ clubId: this.$store.state.Clubs.club });
      } catch (error) {
        console.error("Failed to load club-person relationships:", error);
      }
    },
    openDialog(item = {}) {
      const safeItem = item || {};

      this.form = {
        ...safeItem,
        clubId: this.club,
        role: "Athlete",
        person: safeItem.person
          ? { ...safeItem.person }
          : { personId: null, firstName: "", lastName: "" },
        event: safeItem.event ? { ...safeItem.event } : { eventId: null },
      };

      this.form.person = this.form.person || {};
      this.form.event = this.form.event || {};
      this.form.group = this.form.group || {};

      if (hasJsonStructure(this.form.participationDays)) {
        this.form.participationDays = JSON.parse(this.form.participationDays);
      } else if (Array.isArray(this.form.participationDays)) {
        this.form.participationDays = this.form.participationDays;
      } else {
        this.form.participationDays = [];
      }

      if (this.form.tryoutDatetime) {
        const [date, time] = this.form.tryoutDatetime.split("T");
        if (date) {
          this.form.tryoutDate = date;
        }
        if (time) {
          this.form.tryoutTime = time.substring(0, 5);
        }
      }

      // ✅ FIX: Clean triggers from the actual data that will be saved
      if (this.form.additionalInfo) {
        try {
          const parsed = JSON.parse(this.form.additionalInfo);
          if (Array.isArray(parsed)) {
            // Clean triggers from JSON format
            const cleanedHistory = parsed.map(entry => ({
              ...entry,
              message: entry.message.replace(/\{([^}]+)\}/g, '{~$1~executed}')
            }));
            this.form.additionalInfo = JSON.stringify(cleanedHistory);
          } else {
            // Not an array, treat as single entry
            this.form.additionalInfo = this.form.additionalInfo.replace(/\{([^}]+)\}/g, '{~$1~executed}');
          }
        } catch (e) {
          // Legacy format - clean directly
          this.form.additionalInfo = this.form.additionalInfo.replace(/\{([^}]+)\}/g, '{~$1~executed}');
        }
      }

      // Clear new communication input
      this.newCommunication = '';

      this.dialog = true;
    },
    // ✅ ADD: Format date for display
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    },
    // ✅ ADD: Add new communication to history
    addCommunicationEntry() {
      if (!this.newCommunication.trim()) return;
      
      let history = [];
      
      // Get existing history
      if (this.form.additionalInfo) {
        try {
          const parsed = JSON.parse(this.form.additionalInfo);
          if (Array.isArray(parsed)) {
            history = parsed;
          }
        } catch (e) {
          // Convert legacy format to new format
          if (this.form.additionalInfo.trim()) {
            history = [{
              username: 'Legacy Entry',
              date: this.form.updateDate || this.form.createDate || new Date().toISOString(),
              message: this.form.additionalInfo
            }];
          }
        }
      }
      
      // Add new entry
      const newEntry = {
        username: this.user.userName || 'Unknown User',
        date: new Date().toISOString(),
        message: this.newCommunication.trim()
      };
      
      history.unshift(newEntry); // Add to beginning (latest first)
      
      // Update form data
      this.form.additionalInfo = JSON.stringify(history);
      
      // Clear the input
      this.newCommunication = '';
    },
    // ✅ UPDATED: Modified saveRel method
    saveRel() {
      // ✅ This automatically adds communication when saving
      if (this.newCommunication.trim()) {
        this.addCommunicationEntry();
      }

      // Rest of your existing saveRel logic...
      this.form.updateSource = this.user.userName;
      this.form.formsSigned = null;
      
      const time = typeof this.form.tryoutTime !== "undefined" &&
          this.form.tryoutTime !== null &&
          this.form.tryoutTime !== ""
            ? this.form.tryoutTime
            : "00:00";

      if (this.form.tryoutDate && time) {
        const datetime = new Date(`${this.form.tryoutDate} ${time}`);
        this.form.tryoutDatetime = this.dateWithoutTimezone(datetime);
      } else {
        this.form.tryoutDatetime = null;
      }

      this.form.participationDays =
        Array.isArray(this.form.participationDays) &&
        this.form.participationDays.length > 0
          ? JSON.stringify(this.form.participationDays)
          : null;
          
      this.form.personId = this.personId
        ? this.personId
        : this.form.person.personId;
      this.form.eventId = this.form.event.eventId;
      
      this.clubPersonRelMerge(this.form).then(() => {
        this.dialog = false;
        this.loadClubPersonRel();
      });
    },
    confirmDelete(seqNo) {
      if (confirm("Are you sure you want to delete this relationship?")) {
        this.clubPersonRelDelete({ seqNo });
      }
    },
    showFormsSigned(formsSigned) {
      if (!formsSigned) return;
      this.selectedFormsSigned = formsSigned;
      this.formsSignedDialog = true;
    },
    onEventSelected(event) {
    
      if (event) {
        this.form.startMembership = event.startDate || null;
        this.form.endMembership = event.endDate || null;
      } else {
        this.form.startMembership = null;
        this.form.endMembership = null;
      }
    },
    dateWithoutTimezone(date) {
      // Get the timezone offset in milliseconds
      //console.log(date);
      //console.log(Object.prototype.toString.call(date));
      const tzoffset = date.getTimezoneOffset() * 60000; // offset in milliseconds

      // Subtract the timezone offset to get the UTC equivalent of the date
      const withoutTimezone = new Date(date.valueOf() - tzoffset)
        .toISOString() // Convert to ISO string (which is in UTC)
        .slice(0, -1); // Remove the 'Z' (since you don't want the 'Z' for UTC)
      //console.log(withoutTimezone);
      return withoutTimezone; // This will be the date without timezone applied
    },

    parseParticipationDays,
    getWorkoutScheduleById,
    getCommunicationCount,    
    getLatestCommunication,

    // ✅ ADD: System triggers functionality
    getSystemTriggers,
    extractTriggersFromMessage,
    highlightTriggersInMessage,
    
    insertTrigger(trigger) {
      const textarea = this.$refs.communicationTextarea.$refs.input;
      if (!textarea) return;
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = this.newCommunication || '';
      
      // Insert trigger at cursor position
      const triggerText = `{${trigger}}`;
      this.newCommunication = text.substring(0, start) + triggerText + text.substring(end);
      
      // Set cursor after inserted trigger
      this.$nextTick(() => {
        const newPosition = start + triggerText.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      });
    },
    
    getEntryTriggers(message) {
      return extractTriggersFromMessage(message, this.availableSystemTriggers);
    },
  }
};
</script>
<style scoped>
.communication-history {
  max-height: 300px;
  overflow-y: auto;
}

.communication-entry {
  padding: 8px 0;
}

.communication-entry:last-child {
  margin-bottom: 0 !important;
}

.text--secondary {
  color: #666 !important;
}

/* ✅ ADD: CSS for tooltip line breaks */
.communication-tooltip {
  white-space: pre-line; /* This makes \n line breaks work */
  max-width: 400px;
  word-wrap: break-word;
}

.trigger-chips {
  max-height: 150px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  transform: scale(1.05);
  transition: transform 0.2s;
}

/* Trigger highlighting in messages */
.communication-entry .text-body-2 span {
  border-radius: 4px;
  padding: 2px 4px;
}
</style>